/**
 * Une ligne de catalogue.
 *
 * `lesson_count` est le nombre de leçons **publiées** — c'est le serveur qui
 * l'a décidé (docs/11 §6). Annoncer les brouillons ferait une promesse que le
 * cours ne tient pas, et l'écran n'a pas les données pour trancher lui-même.
 */
import { useTranslation } from "react-i18next";

import type { CourseSummary } from "@/features/catalog/api/useCatalog";
import { StatusBadge } from "@/features/catalog/components/StatusBadge";

interface CourseCardProps {
  course: CourseSummary;
  domainName?: string;
  showStatus?: boolean;
}

export function CourseCard({ course, domainName, showStatus = false }: CourseCardProps) {
  const { t } = useTranslation("catalog");

  return (
    <article className="hover:border-brand-400 h-full rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-colors dark:border-slate-800 dark:bg-slate-900">
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{course.title}</h3>
        {showStatus && <StatusBadge status={course.status} />}
      </div>
      {course.subtitle !== null && (
        <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">{course.subtitle}</p>
      )}
      <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        {domainName !== undefined && domainName !== "" && (
          <div>
            <dt className="sr-only">{t("catalog.domain")}</dt>
            <dd>{domainName}</dd>
          </div>
        )}
        {course.level !== null && (
          <div>
            <dt className="sr-only">{t("catalog.level")}</dt>
            <dd>{t(`levels.${course.level}`)}</dd>
          </div>
        )}
        <div>
          <dt className="sr-only">{t("catalog.lessons")}</dt>
          <dd>{t("catalog.lessonCount", { count: course.lesson_count })}</dd>
        </div>
      </dl>
      {course.tags.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1">
          {course.tags.map((tag) => (
            <li
              key={tag}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            >
              {tag}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
